import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OwenerTransferModel } from 'ts/transfer/classes/OwenerTransferModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import BookingPreviewElement from './BookingPreviewElement';
import { getCurrency, formatPriceCurrency, showError } from 'utils/utils';
import { useNavigate } from 'react-router-dom';
import { bookComplete } from 'services/transfer';
import { BookingModel } from 'ts/transfer/interfaces/TransferResultsResponse';
import BookPayment from 'components/shared/BookPayment';
import { Credit2000PaymentInfo } from 'ts/shared/interfaces/Generic';
import HandleFee from 'components/shared/HandleFee';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface TransferBookingPreviewProps {
  bookingModel: BookingModel;
  ownerModel: OwenerTransferModel;
  isSuccess?: boolean;
  onClickBook?: () => void;
  tripId: string;
  companyId: string;
  couponAmount: number;
}

const TransferBookingPreview: FC<TransferBookingPreviewProps> = ({
  ownerModel,
  bookingModel,
  ...props
}) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const submit = async (cred: Credit2000PaymentInfo) => {
    setLoading(true);
    await props.onClickBook?.();

    const response = await bookComplete({
      ...bookingModel,
      creditCard: true,
      paymentError: '',
      paymentSettings: cred,
      paxes: bookingModel.paxes.map((pax) => ({ ...pax, gender: 'M' })),
      credit2000PaymentInfo: cred,
      couponAmount: props.couponAmount,
      companyId: props.companyId,
      tripId: props.tripId,
    });

    if (response) {
      if (response.sessionExpired) {
        showError(t('home.session_expired'));
      } else if (response.hasErrors) {
        showError(response.errorMessage);
      } else {
        sessionStorage.removeItem('screenshotsTransfer');
        sessionStorage.setItem('transferPaymentOrder', JSON.stringify(response));
        navigate('/transfer/payment');
      }
    }
    setLoading(false);
  };

  const sendRequest = async () => {
    setLoading(true);
    await props.onClickBook?.();

    const response = await bookComplete({
      ...bookingModel,
      creditCard: false,
      paymentError: '',
      paymentSettings: bookingModel.credit2000PaymentInfo,
      paxes: bookingModel.paxes.map((pax) => ({ ...pax, gender: 'M' })),
      couponAmount: props.couponAmount,
      companyId: props.companyId,
      tripId: props.tripId,
    });
    if (response?.bookingSuccess) {
      sessionStorage.removeItem('screenshotsTransfer');
      sessionStorage.setItem('transferSuccessOrder', JSON.stringify(response.data));
      navigate('/transfer/success');
    } else {
      showError(response?.errorMessage);
    }
    setLoading(false);
  };

  return (
    <div className="transfer-results-data">
      <div className="book-form default-shadow mb-15">
        <div className="title-underline title-header-grey text-lg mb-20">
          {t('flight.order_summary')}
        </div>
        <div className="title text-md mb-10 text-contrast">
          <FontAwesomeIcon className="mr-5" icon={faUser} size="sm" />
          {t('passenger.details')}
        </div>
        {!!ownerModel && (
          <div className="flight-details-info">
            <div className="package-details-block">
              <span className="package-details-title">{t('passenger.fn')}</span>
              <span className="package-details-text">{ownerModel.firstName}</span>
            </div>
            <div className="package-details-block">
              <span className="package-details-title">{t('passenger.ln')}</span>
              <span className="package-details-text">{ownerModel.lastName}</span>
            </div>
            <div className="package-details-block">
              <span className="package-details-title">{t('passenger.email')}</span>
              <span className="package-details-text">{ownerModel.email}</span>
            </div>
            <div className="package-details-block">
              <span className="package-details-title">{t('passenger.phone')}</span>
              <span className="package-details-text">{ownerModel.phone}</span>
            </div>
            {!!ownerModel.message && (
              <div className="package-details-block">
                <span className="package-details-title">{t('passenger.message')}</span>
                <span className="package-details-text">{ownerModel.message}</span>
              </div>
            )}
            <div className="divider mt-20 mb-20"></div>
          </div>
        )}

        <BookingPreviewElement data={bookingModel.forward} title="transfer.arrival_details" />
        {bookingModel.return && (
          <BookingPreviewElement data={bookingModel.return} title="transfer.return_details" />
        )}
        <HandleFee name="transfers" />
        <div className="flex-row-end text-lg">
          <span className="mr-5">{t('total')}:</span>
          <span className="text-bold">
            {formatPriceCurrency(bookingModel.totalPrice, getCurrency(bookingModel.currency))}
          </span>
        </div>
        {!props.isSuccess && (
          <BookPayment
            onClickSend={sendRequest}
            onClickSubmit={submit}
            disabled={loading}
            privileges={bookingModel.privilegesInfo}
            credit2000={bookingModel.credit2000PaymentInfo}
          />
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(TransferBookingPreview, 'TransferBookingPreview');
